import Logo from "~/components/Logo"
import {
  accompanyingPrevious,
  articleBodyStyle,
  articleCompareImages,
  articleFaqBodyStyle,
  articleImageStyle,
  bold,
  faqGrid,
  faqH3Style,
  faqTwoCol,
  figCaptionStyle,
  h1Style,
  h1Wrap,
  h2Style,
  h3Style,
  headerTextStyle,
  stepSectionStyle,
  unorderedList,
} from "./article.css"
import { MetaFunction } from "@remix-run/node"
import StickyHeader from "~/components/StickyHeader"
import { page } from "~/shared/styles.css"
import LinkButton from "~/components/LinkButton"
import Link from "~/components/Link"
import Footer from "~/components/Footer"
import { golden, vars } from "~/components/styles/theme.css"
import { Figure, H2, P, Section } from "~/components/articleComponents"
import ScrollyWolly from "~/components/ScrollyWolly"

const TITLE = "Small Garden Ideas: Transform Any Outdoor Space | Rescape AI"
const DESCRIPTION =
  "Discover inspiring small garden ideas for 2025. From patio planting to clever layouts, use Rescape AI to visualise your perfect small outdoor space instantly."
const URL = "https://rescapeai.com/small-garden-ideas"
const IMAGE =
  "https://rescapeai.com/articles/248d8f94-104f-48c0-88bd-4b36cea2c8213-cropped.webp"

export const meta: MetaFunction = () => {
  return [
    {
      title: TITLE,
    },
    {
      name: "description",
      content: DESCRIPTION,
    },
    {
      property: "og:title",
      content: TITLE,
    },
    {
      property: "og:description",
      content: DESCRIPTION,
    },
    {
      property: "og:image",
      content: IMAGE,
    },
    // SEO FIX: Added Twitter Card tags
    {
      name: "twitter:card",
      content: "summary_large_image",
    },
    {
      name: "twitter:title",
      content: TITLE,
    },
    {
      name: "twitter:description",
      content: DESCRIPTION,
    },
    {
      name: "twitter:image",
      content: IMAGE,
    },
    {
      tagName: "link",
      rel: "canonical",
      href: URL,
    },
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: TITLE,
        description: DESCRIPTION,
        author: {
          "@type": "Organization",
          name: "Rescape AI",
        },
        datePublished: "2026-04-16",
        dateModified: "2026-04-16",
        publisher: {
          "@type": "Organization",
          name: "Rescape AI",
          logo: {
            "@type": "ImageObject",
            url: "https://rescapeai.com/logo.webp",
          },
        },
        image: IMAGE,
        mainEntity: {
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "How do I design a small garden on a budget?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Focus on one statement area rather than trying to do everything at once. New paving or a well-planted raised bed makes more impact than spreading budget thinly. Rescape AI can help you test ideas before you spend anything.",
              },
            },
            {
              "@type": "Question",
              name: "Can I use Rescape AI for a small garden?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes. Rescape AI works with any outdoor space, regardless of size. Upload a photo of your garden and generate design ideas instantly.",
              },
            },
            {
              "@type": "Question",
              name: "How do I make a small patio garden look good?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Keep hard surfaces clean and consistent in material. Use large pots rather than many small ones. Add height with a climber or a trained wall shrub. Keep furniture minimal and proportionate.",
              },
            },
            {
              "@type": "Question",
              name: "Is a small garden worth designing properly?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Absolutely. A well-designed small garden is often more usable and visually satisfying than a larger, poorly considered one. Good design is about proportion and intention, not size.",
              },
            },
          ],
        },
      },
    },
  ]
}

function Index() {
  return (
    <>
      <div
        className={h1Style}
        style={{
          textAlign: "center",
          backgroundImage:
            "url(/articles/248d8f94-104f-48c0-88bd-4b36cea2c8213-cropped.webp)",
          backgroundPositionY: "50%",
        }}
      >
        <div className={h1Wrap}>
          <Logo width="108px" />
          <h1 className={headerTextStyle}>
            Small Garden Ideas That Make Every Inch Count
          </h1>
        </div>
      </div>
      <StickyHeader status="SignUp" />

      <div className={page}>
        <section className={stepSectionStyle}>
          <P>
            A small garden is not a limitation. It is an opportunity. Whether you have a
            narrow strip behind a terraced house, a compact courtyard, or a modest patch
            of lawn, the right design choices can turn limited space into something
            genuinely beautiful.
          </P>
          <P>
            The challenge most homeowners face is not a lack of ideas, it is picturing how
            those ideas will actually look in their specific garden. That is exactly what
            Rescape AI is built for. Upload a photo of your outdoor space and generate
            professional design visuals in seconds, no landscaping experience required.
          </P>
          <figure>
            <ScrollyWolly
              height={`${golden * 10}rem`}
              yPosition="50%"
              marginTop="1rem"
              before={{
                image: "/articles/small-garden-48e76eac.webp",
                title: "Patio",
                alt: "Small Garden with Patio and Flowerbeds",
              }}
              after={{
                image: "/articles/small-garden-bd7820e3.webp",
                title: "Seating area",
                alt: "Small Garden with a Relaxing Seating Area",
              }}
            />
            <figcaption className={figCaptionStyle}>
              Examples of small garden designs generated on{" "}
              <Link href="/hello/signup">Rescape AI</Link>
            </figcaption>
          </figure>
        </section>
        <Section>
          <H2>Low Maintenance Small Garden Ideas</H2>
          <P>
            If time is limited, the design needs to do some of the work for you. These
            approaches keep a small garden looking good without constant upkeep.
          </P>
          <P>
            Gravel or paved surfaces eliminate the need to mow and edge. A single
            well-planted border alongside a hard-landscaped base is far easier to manage
            than a small lawn surrounded by fiddly edging. Choose gravel in a tone that
            complements your house, not one that dates it.
          </P>
          <P>
            Evergreen planting is your friend. Structural plants like box, pittosporum,
            and phormium hold their shape year-round and require little attention. Mix in
            one or two seasonal performers, an allium flush in spring, a late-summer
            grass, to add movement without adding work.
          </P>
          <P>
            Raised beds contain weeding, improve drainage, and define zones clearly. They
            also add physical structure to a space that might otherwise feel flat.
          </P>
        </Section>
        <Section>
          <H2>Modern Small Garden Design Ideas</H2>
          <P>
            Contemporary small gardens tend to prioritise clean lines, quality materials,
            and a limited plant palette. Less is more. A single species planted in
            repetition looks more deliberate and impactful than a busy mix of everything.{" "}
          </P>
          <Figure
            imgStyle={{ maxHeight: "360px", objectFit: "cover" }}
            style={{ marginTop: "0.5rem" }}
            src="/articles/small-garden-c866731a.webp"
            caption="Intricate, circular raised planter small garden design | Designed on Rescape AI"
          />
          <P>
            Porcelain paving tiles have become a popular choice for modern schemes. They
            are durable, easy to clean, and available in large formats that make a small
            space feel more expansive. Pair with steel edging and architectural planting
            for a considered, high-end finish.
          </P>
          <P>
            Corten steel planters, simple timber pergolas, and recessed lighting are
            details that elevate a small garden from functional to designed. Used
            sparingly, they signal intent without overwhelming the space.
          </P>
        </Section>
        <Section>
          <H2>Small Front Garden Ideas</H2>
          <P>
            Front gardens are often the most neglected because they are the most exposed.
            A few plants in pots, a maintained hedge, or even a simple gravel bed with a
            statement shrub can dramatically improve kerb appeal.
          </P>
          <Figure
            imgStyle={{ maxHeight: "360px", objectFit: "cover" }}
            style={{ marginTop: "0.5rem" }}
            src="/articles/small-garden-0ed3562e.webp"
            caption="Cosy small garden with patio and seating area | Designed on Rescape AI"
          />
          <P>
            If you have parking to consider, permeable paving over a planted border is
            both practical and visually far better than a solid tarmac or concrete finish.
            A narrow planting strip along the fence or boundary keeps the space from
            feeling entirely hard.
          </P>
          <P>
            Rescape AI can be used on front garden photos just as easily as back gardens.
            If you are not sure how a hedge, a gate style, or a new planting bed would
            look, generate a few variations before committing to any work.
          </P>
        </Section>
        <Section>
          <H2>Use AI to Visualise Your Small Garden Design</H2>
          <P>
            The hardest part of small garden planning is committing to changes before you
            can see how they will look. Rescape AI removes that barrier. Upload a
            photograph of your existing garden — any size, any condition, choose a style
            direction, and generate realistic design visualisations in seconds.
          </P>
          <P>
            You can experiment with without spending a penny on{" "}
            <Link href="/ai-landscape-design">landscaping.</Link> It is the fastest way to
            move from vague ideas to a clear design direction.
          </P>
        </Section>
        <Section>
          <div style={{ display: "flex", justifyContent: "center", marginTop: "1rem" }}>
            <LinkButton url="/hello/signup">Try Rescape AI</LinkButton>
          </div>
        </Section>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>Frequently Asked Questions</h2>
          <div className={`${faqGrid} ${faqTwoCol}`}>
            <div>
              <h3 className={faqH3Style}>How do I design a small garden on a budget?</h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                Focus on one statement area rather than trying to do everything at once.
                New paving or a well-planted raised bed makes more impact than spreading
                budget thinly. Rescape AI can help you test ideas before you spend
                anything.
              </p>
            </div>
            <div>
              <h3 className={faqH3Style}>Can I use Rescape AI for a small garden?</h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                Yes. Rescape AI works with any outdoor space, regardless of size. Upload a
                photo of your garden and generate design ideas instantly.
              </p>
            </div>
            <div>
              <h3 className={faqH3Style}>
                How do I make a small patio garden look good?
              </h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                Keep hard surfaces clean and consistent in material. Use large pots rather
                than many small ones. Add height with a climber or a trained wall shrub.
                Keep furniture minimal and proportionate.
              </p>
            </div>
            <div>
              <h3 className={faqH3Style}>Is a small garden worth designing properly?</h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                Absolutely. A well-designed small garden is often more usable and visually
                satisfying than a larger, poorly considered one. Good design is about
                proportion and intention, not size.
              </p>
            </div>
          </div>
        </section>
        <div style={{ display: "flex", justifyContent: "center", marginTop: "1rem" }}>
          <LinkButton url="/hello/signup">Join Rescape AI</LinkButton>
        </div>
        <Footer showLinks={false} />
      </div>
    </>
  )
}

export default Index
